'use client';

import { useEffect, useMemo, useState } from 'react';
import { Icon, useTranslation } from '@weareconceptstudio/core';
import { useCart } from '@weareconceptstudio/cart';
import { useAccountContext, useAddressContext } from '@weareconceptstudio/account';
import StickyDeliveryDropdown from './StickyDeliveryDropdown';
import { formatAddressTitle, normalizeBranchText } from './utils';
import { getHasFulfillmentSelection, readPendingAddress } from '@/helpers/fulfillmentGuard';

const StickyDeliveryBar = ({ globalData }) => {
	const { translate } = useTranslation();
	const { checkoutData } = useCart();
	const { fulfillment, useUser } = useAccountContext();
	const { isLoggedIn } = useUser();
	const { selectedAddresses, addresses, hasAddressType } = useAddressContext();
	const [pendingAddress, setPendingAddress] = useState(readPendingAddress);

	useEffect(() => {
		const handler = (event) => setPendingAddress(event.detail || null);
		window.addEventListener('pendingAddressSaved', handler);
		return () => window.removeEventListener('pendingAddressSaved', handler);
	}, []);

	const isTakeaway = checkoutData?.fulfillmentType === 'takeaway';
	const branches = fulfillment?.branches || [];

	const selectedBranch = useMemo(() => branches.find((b) => Number(b.id) === Number(checkoutData?.fulfillmentBranchId)) || branches[0] || null, [branches, checkoutData?.fulfillmentBranchId]);

	const selectedAddress = useMemo(() => {
		if (hasAddressType) return selectedAddresses?.shipping || null;
		const list = Array.isArray(addresses) ? addresses : [];
		const id = selectedAddresses?.id;
		return id ? list.find((a) => a.id === id) : list[0] || null;
	}, [hasAddressType, selectedAddresses, addresses]);

	const hasFulfillmentSelection = useMemo(
		() =>
			getHasFulfillmentSelection({
				checkoutData,
				selectedAddresses,
				hasAddressType,
				isLoggedIn,
				branches,
				pendingAddress,
			}),
		[checkoutData, selectedAddresses, hasAddressType, isLoggedIn, branches, pendingAddress]
	);

	const pillLabel = useMemo(() => {
		if (!hasFulfillmentSelection) {
			return translate('deliveryTemplate.addressTakeawayPlaceholder');
		}
		if (isTakeaway) {
			return selectedBranch ? normalizeBranchText(selectedBranch.name) : translate('deliveryTemplate.selectShopLocation');
		}
		if (selectedAddress) {
			return formatAddressTitle(selectedAddress);
		}
		if (!isLoggedIn && pendingAddress) {
			return pendingAddress.address || pendingAddress.address_line_1 || pendingAddress.name || translate('deliveryTemplate.whatsYourAddressPlaceholder');
		}
		return translate('deliveryTemplate.whatsYourAddressPlaceholder');
	}, [hasFulfillmentSelection, isTakeaway, selectedBranch, selectedAddress, isLoggedIn, pendingAddress, translate]);

	const initialTab = isTakeaway ? 'takeaway' : 'delivery';

	return (
		<StickyDeliveryDropdown
			globalData={globalData}
			initialTab={initialTab}
			redirectAfterConfirm
			className='is-sticky-portal'
			trigger={({ isOpen, togglePanel }) => (
				<button
					type='button'
					className='sticky-pill'
					aria-expanded={isOpen}
					onClick={togglePanel}
				>
					<Icon
						name='location'
						className='icon-pin'
					/>
					<span className='selector-text font-montserrat-arm-regular background-color'>{pillLabel}</span>
					<Icon
						name='right-arrow'
						className={`icon-arrow ${isOpen ? 'is-open' : ''}`}
					/>
				</button>
			)}
		/>
	);
};

export default StickyDeliveryBar;
